<template>
  <div class="quickbooks-company">
    <div class="quickbooks-company-container">
      <Alert
        v-if="company.errors.length"
        :text="company.errors[0]"
        type="error"
      />
      <div class="name full-width mb-2">{{ company.name }}</div>
      <fieldset>
        <legend class="flex gap-1 items-center">
          Settings
          <SeraButton
            v-if="!refreshingSettings"
            link
            icon="fa-regular fa-refresh"
            tooltip="Refresh Settings"
            class="ml-1"
            @click="handleRefreshSettings"
          ></SeraButton>
        </legend>
        <div v-if="refreshingSettings">
          <Spinner message="Refreshing company settings..." />
        </div>
        <div v-else class="settings">
          <div class="mb-1">
            <span class="field-label">Edition: </span>{{ company.edition }}
          </div>
          <div class="mb-1">
            <span class="field-label">Class Tracking: </span
            >{{ trackClassesBy }}
          </div>
          <div v-if="trackClasses" class="flex flex-col md:flex-row gap-3">
            <span class="field-label whitespace-nowrap"
              >Accounting Classes:
            </span>
            <AccountingClassStatus
              status="pending"
              :statusCount="store.getters.pendingAccountingClasses"
            />
            <AccountingClassStatus
              status="verified"
              :statusCount="store.getters.verifiedAccountingClasses"
            />
            <AccountingClassStatus
              status="inactive"
              :statusCount="store.getters.inactiveAccountingClasses"
            />
            <a href="/company/settings?tab=cs_Accounting%20Settings">
              <i class="fa-regular fa-pencil mr-1" />
              Manage
            </a>
          </div>
        </div>
      </fieldset>
      <fieldset>
        <legend>Accounts</legend>
        <AccountEditor
          v-for="accountType in accountTypes"
          :key="accountType.field_name"
          :company-id="company.id"
          :field-name="accountType.field_name"
          :field-label="accountType.field_label"
          :tooltip-text="accountType.tooltip_text"
          :account-name="company[`${accountType.field_name}_name`]"
          class="full-width"
        />
      </fieldset>
      <div class="d-flex mt-4">
        <div class="d-flex align-items-center ml-auto">
          <SeraButton
            v-if="company.needs_reauth"
            class="mr-2"
            text="Reauthenticate"
            @click="linkCompany"
          />
          <SeraButton
            outline
            text="Disconnect from QuickBooks"
            @click="handleShowUnlinkCompanyConfirm"
          />
        </div>
      </div>
    </div>
    <sera-modal
      :open="confirmModalOpen"
      @primary-action="handleUnlinkCompany"
      @close="handleUnlinkConfirmClose"
    >
      <template #header>
        <div>
          <h3 class="page-header-2">Disconnect Company</h3>
        </div>
      </template>
      <div style="max-width: 400px">
        <p class="mb-4">
          Are you sure you want to disconnect
          <strong>{{ company.name }}</strong
          >?<br /><br />
          Once disconnected, you will no longer be able to send accounting data
          to QuickBooks for this company.<br /><br />
          You can restore your connection by reconnecting to the same company
          once disconnected.<br /><br />
          If you need to connect to a different QuickBooks company, please
          contact Sera customer support.
        </p>
      </div>
    </sera-modal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import SeraButton from "@/components/Button.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Spinner from "@/components/Spinner.vue";
import AccountEditor from "./AccountEditor.vue";
import AccountingClassStatus from "./AccountingClassStatus.vue";
import { QuickbooksCompany } from "./types";
import { injectUseQuickbooksConfig } from "./useQuickbooksConfig";

const store = useStore();

const props = defineProps({
  company: {
    type: Object as () => QuickbooksCompany,
    default: () => {},
    required: true
  }
});

const confirmModalOpen = ref(false);

const {
  linkCompany,
  unlinkCompany,
  fetchQuickbooksAccountTypes,
  accountTypes,
  refreshSettings,
  refreshingSettings
} = injectUseQuickbooksConfig();

async function handleRefreshSettings() {
  return refreshSettings(props.company);
}

async function handleUnlinkCompany() {
  return unlinkCompany(props.company);
}

function handleShowUnlinkCompanyConfirm() {
  confirmModalOpen.value = true;
}

function handleUnlinkConfirmClose() {
  confirmModalOpen.value = false;
}

const trackClasses = computed(() => {
  return props.company.track_classes_by !== "disabled";
});

const trackClassesBy = computed(() => {
  if (props.company.track_classes_by === "disabled") return "Disabled";
  if (props.company.track_classes_by === "invoice")
    return "One to entire transaction";
  if (props.company.track_classes_by === "line_item")
    return "One to each row in a transaction";

  return props.company.track_classes_by;
});

onMounted(async () => {
  try {
    await fetchQuickbooksAccountTypes();

    await store.dispatch("fetchAccountingClasses");
  } catch (e) {
    // Do nothing
  }
});
</script>

<style scoped lang="scss">
.quickbooks-company-container {
  .name {
    font-size: 17px;
    font-weight: bold;
  }

  .settings-title {
    font-size: 15px;
    font-weight: bold;
  }

  > * {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  fieldset {
    border-radius: 6px;
    border: 1px solid var(--sera-color-fields-border);
    padding: 10px;
  }

  legend {
    width: auto;
    font-size: 15px;
    font-weight: bold;
    margin: 0;
    padding: 0 5px;
  }
}
</style>
